import React, { useEffect, useRef, useCallback } from 'react';
import PropTypes from 'prop-types';
import cn from 'classnames';
import isEmpty from 'lodash/isEmpty';
import isFunction from 'lodash/isFunction';

import { DIRECTION_LEFT, DIRECTION_RIGHT, DIRECTION_TOP, DIRECTION_BOTTOM } from './constants';

import './Resizable.theme.scss';

/* eslint-disable no-unused-expressions */
/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable no-use-before-define */

function isHorizontal(direction) {
  return direction === DIRECTION_LEFT || direction === DIRECTION_RIGHT;
}

const getElRect = (el) => {
  // Or window.getComputedStyle(el).width, window.getComputedStyle(el).height
  const rect = el.getBoundingClientRect();
  return {
    width: rect.width,
    height: rect.height,
  };
};

const getEventPoint = (e) => {
  const event = e.touches ? e.touches[0] : e;
  return (
    event && {
      x: event.clientX,
      y: event.clientY,
    }
  );
};

// eslint-disable-next-line react/prop-types
function Resizable({ className, direction, constraints, useFlexBasis, onResize, onDragEnd, children }) {
  const containerRef = useRef(null);
  const startPointRect = useRef({ x: 0, y: 0, width: 0, height: 0 });
  const dragRect = useRef({ width: 0, height: 0 });

  useEffect(() => {
    // Update according to constraints on mount
    if (!isEmpty(constraints)) {
      Object.assign(startPointRect.current, {
        ...getElRect(containerRef.current),
      });
      updateSize();
    }

    // Remove listeners on unMount just in case
    return () => {
      document.removeEventListener('mousemove', onDragging, false);
      document.removeEventListener('touchmove', onDragging, false);
      document.removeEventListener('mouseup', onDragEndCallback, false);
      document.removeEventListener('touchend', onDragEndCallback, false);
    };
  }, []);

  // Manipulate directly with DOM, for performance reasons
  const applyStyleSize = (style, size) => {
    if (useFlexBasis) {
      containerRef.current.style['flex-basis'] = `${size}px`;
    } else {
      containerRef.current.style[style] = `${size}px`;
    }

    isFunction(onResize) && onResize(size);
  };

  const updateSize = () => {
    const { width: startWidth, height: startHeight } = startPointRect.current;
    const { width: dragWidth, height: dragHeight } = dragRect.current;

    if (isHorizontal(direction)) {
      const { widthMin, widthMax } = constraints;
      let width = startWidth + dragWidth;

      if (widthMin !== undefined && width < widthMin) {
        width = widthMin;
      } else if (widthMax !== undefined && width > widthMax) {
        width = widthMax;
      }

      applyStyleSize('width', width);
    } else {
      const { heightMin, heightMax } = constraints;
      let height = startHeight + dragHeight;

      if (heightMin !== undefined && height < heightMin) {
        height = heightMin;
      } else if (heightMax !== undefined && height > heightMax) {
        height = heightMax;
      }

      applyStyleSize('height', height);
    }
  };

  const onDragStart = useCallback((e) => {
    Object.assign(startPointRect.current, {
      ...getEventPoint(e),
      ...getElRect(containerRef.current),
    });

    document.addEventListener('mousemove', onDragging, false);
    document.addEventListener('touchmove', onDragging, false);
    document.addEventListener('mouseup', onDragEndCallback, false);
    document.addEventListener('touchend', onDragEndCallback, false);
  }, []);

  const onDragging = useCallback((e) => {
    e && e.preventDefault && e.preventDefault();

    const { x, y } = getEventPoint(e);
    const { x: startX, y: startY } = startPointRect.current;

    Object.assign(dragRect.current, {
      width: startX - x,
      height: startY - y,
    });

    updateSize();
  }, []);

  const onDragEndCallback = useCallback(() => {
    document.removeEventListener('mousemove', onDragging, false);
    document.removeEventListener('touchmove', onDragging, false);
    document.removeEventListener('mouseup', onDragEndCallback, false);
    document.removeEventListener('touchend', onDragEndCallback, false);
  }, []);

  return (
    <div className={cn('rx-resizable', className)} ref={containerRef}>
      {/* eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions */}
      <div
        role="separator"
        className={`rx-resizable__handler rx-resizable__handler--${direction}`}
        onMouseDown={onDragStart}
        onTouchStart={onDragStart}
        // need separate call since useCallback is caching AA parameters
        onTouchEnd={() => {
          isFunction(onDragEnd) && onDragEnd();
        }}
        onMouseUp={() => {
          isFunction(onDragEnd) && onDragEnd();
        }}
      />
      {children}
    </div>
  );
}

Resizable.defaultProps = {
  // eslint-disable-next-line react/default-props-match-prop-types
  direction: DIRECTION_TOP,
  constraints: {},
  useFlexBasis: false,
};

Resizable.propTypes = {
  className: PropTypes.string,
  direction: PropTypes.oneOf([DIRECTION_LEFT, DIRECTION_RIGHT, DIRECTION_TOP, DIRECTION_BOTTOM]).isRequired,
  constraints: PropTypes.shape({
    widthMin: PropTypes.number,
    widthMax: PropTypes.number,
    heightMin: PropTypes.number,
    heightMax: PropTypes.number,
  }),
  useFlexBasis: PropTypes.bool,
  onResize: PropTypes.func,
  onDragEnd: PropTypes.func,
};

export default Resizable;
